import { useQuery } from "@apollo/client";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import Print from "@mui/icons-material/PrintOutlined";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { GetWasteContainerQuery, ReconcileChoice } from "generated-graphql/graphql";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { useDeferredValue, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { WasteStreamDetailsModalSkeleton } from "../Facilities/Facility/WasteStreamDetails/WasteStreamDetailsSkeleton";
import { WASTE_STREAM_DRAWER_WIDTH } from "../Facilities/Facility/WasteStreamDetails/wasteStreamDrawerWidth";
import { WasteClassificationChip } from "../WasteClassificationChip";
import { WasteStreamPanel } from "../WasteStreams/WasteStreamDrawer";
import { AccumulationClockSection } from "./AccumulationClockSection";
import { GET_WASTE_CONTAINER_QUERY } from "./api";
import { ConsolidateContainersDialog } from "./ConsolidateContainersDialog";
import {
  accumulationAreaLabel,
  containerHasShipped,
  containerTypeLabel,
  requestManifestAvailability,
  formatContainerDate,
  formatQuantity,
  STATUS_TRANSITION_OPTIONS,
} from "./containerDisplay";
import { ContainerFillCard } from "./ContainerFillCard";
import { ContainerFillEventDialog } from "./ContainerFillEventDialog";
import { ContainerHistorySection } from "./ContainerHistorySection";
import {
  ContainerDetailsCard,
  ContainerShippingCard,
  ContainerWasteCard,
  ShipmentLink,
} from "./ContainerRecordCards";
import { EmptyContainerDialog } from "./EmptyContainerDialog";
import { FillHistorySection } from "./FillHistorySection";
import { ContainerLabelDialog } from "./Label/ContainerLabelDialog";
import { ContainerLabelSection } from "./Label/ContainerLabelSection";
import { MoveContainerDialog } from "./MoveContainerDialog";
import { SetContainerStatusDialog } from "./SetContainerStatusDialog";
import { WasteContainerDialog } from "./WasteContainerDialog";
import { WasteContainerStatusChip } from "./WasteContainerStatusChip";

type WasteContainerDetailData = NonNullable<GetWasteContainerQuery["wasteContainer"]>;

const CONTAINER_WIDTH_PX = { sm: 640, md: 880, lg: 1048 };

/**
 * Wide enough for the specs' two-column card grid (1032px of content) without
 * covering the list it opened from, so the reader keeps their place in it.
 */
const DRAWER_WIDTH = { xs: "100%", ...CONTAINER_WIDTH_PX };

/**
 * The stream drawer's own width, but never narrower than the container the
 * stream replaces: stepping into the stream shouldn't shrink the panel.
 */
const STREAM_VIEW_WIDTH = {
  xs: "100%",
  sm: `max(${CONTAINER_WIDTH_PX.sm}px, ${WASTE_STREAM_DRAWER_WIDTH})`,
  md: `max(${CONTAINER_WIDTH_PX.md}px, ${WASTE_STREAM_DRAWER_WIDTH})`,
  lg: `max(${CONTAINER_WIDTH_PX.lg}px, ${WASTE_STREAM_DRAWER_WIDTH})`,
};

/**
 * Which container this is and what is in it.
 *
 * The label and where it sits identify the drum; the waste stream is the
 * title, because what a container holds is what a reader came for.
 */
function ContainerIdentity({ container }: { container: WasteContainerDetailData }) {
  const isArchived = !!container.archivedAt;
  // Federal codes, not management method codes: the codes a reader expects
  // beside a container's classification are the ones printed on its label.
  const wasteCodes = container.label.federalWasteCodes;

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={2} useFlexGap flexWrap="wrap">
        <Typography variant="h5" component="h2">
          {container.containerLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {[
            // Size and kind read as one phrase, the way the specs write it:
            // "55 gal steel drum".
            [
              container.capacity != null
                ? formatQuantity(container.capacity, container.capacityUnit)
                : null,
              containerTypeLabel(container.containerType),
            ]
              .filter(Boolean)
              .join(" "),
            container.facility?.name,
            accumulationAreaLabel(container.accumulationArea) || null,
          ]
            .filter(Boolean)
            .join(" • ")}
        </Typography>
      </Stack>
      <Typography variant="h6" component="p" sx={{ mt: 2 }}>
        {container.wasteStream?.name ?? "Uncharacterized"}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
        sx={{ mt: 2 }}
      >
        <WasteContainerStatusChip status={container.status} />
        <WasteClassificationChip
          classification={container.wasteStream?.classification}
          variant="soft"
        />
        {wasteCodes.map((code) => (
          <StatusChip key={code} label={code} color="default" variant="soft" />
        ))}
        {isArchived && <StatusChip label="Archived" color="default" variant="soft" />}
      </Stack>
    </Box>
  );
}

/**
 * The actions that don't fit the header's primary row.
 *
 * Everything here is reachable and none of it is the thing a reader opened the
 * drawer to do — editing the record, or the state changes that only apply to
 * particular containers. Keeping them behind one control lets the primary row
 * stay the four actions the specs draw.
 */
function ContainerOverflowMenu({
  container,
  hasShipped,
  isReadOnly,
  onEdit,
  onConsolidate,
  onEmpty,
  onChangeStatus,
}: {
  container: WasteContainerDetailData;
  /** Consolidating INTO a shipped container is rejected by the container rules. */
  hasShipped: boolean;
  isReadOnly: boolean;
  onEdit: () => void;
  onConsolidate: () => void;
  onEmpty: () => void;
  onChangeStatus: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);
  const hasStatusTransitions = STATUS_TRANSITION_OPTIONS[container.status].length > 0;

  const run = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          aria-label="More container actions"
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <MoreHoriz fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={close}>
        <MenuItem onClick={run(onEdit)} disabled={isReadOnly}>
          Edit
        </MenuItem>
        {!hasShipped && (
          <MenuItem onClick={run(onConsolidate)} disabled={isReadOnly}>
            Consolidate
          </MenuItem>
        )}
        {container.isPermanent && (
          <MenuItem onClick={run(onEmpty)} disabled={isReadOnly}>
            Empty tank
          </MenuItem>
        )}
        {hasStatusTransitions && (
          <MenuItem onClick={run(onChangeStatus)} disabled={isReadOnly}>
            Change status
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

/**
 * The drawer's fixed header: who the container is, and what can be done to it.
 *
 * The action row is the specs' set. Request manifest leads because packaging a
 * full container into a shipment is the reason most readers open this; it
 * hands off to the shipment builder with the container already picked rather
 * than making them find it again in a list.
 */
function ContainerDrawerHeader({
  container,
  isReadOnly,
  onClose,
  onRequestManifest,
  onLogRemoval,
  onMove,
  onPrintLabel,
  onEdit,
  onConsolidate,
  onEmpty,
  onChangeStatus,
}: {
  container: WasteContainerDetailData;
  isReadOnly: boolean;
  onClose: () => void;
  onRequestManifest: () => void;
  onLogRemoval: () => void;
  onMove: () => void;
  onPrintLabel: () => void;
  onEdit: () => void;
  onConsolidate: () => void;
  onEmpty: () => void;
  onChangeStatus: () => void;
}) {
  const isArchived = !!container.archivedAt;
  const requestManifest = requestManifestAvailability(container);
  // Everything that changes where the waste is, or how much of it there is,
  // stops making sense once it has left the site — and the container rules
  // reject the first two outright.
  const hasShipped = containerHasShipped(container);

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <ContainerIdentity container={container} />
          {!isArchived && (
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
              {requestManifest.kind !== "hidden" && (
                // A disabled MUI button swallows pointer events, so the reason
                // has to hang on a wrapper or it never reaches the cursor.
                <Tooltip title={requestManifest.kind === "blocked" ? requestManifest.reason : ""}>
                  <span>
                    <Button
                      size="small"
                      endIcon={<ChevronRight />}
                      disabled={requestManifest.kind === "blocked" || isReadOnly}
                      onClick={onRequestManifest}
                    >
                      Request manifest
                    </Button>
                  </span>
                </Tooltip>
              )}
              {!hasShipped && (
                <Button size="small" onClick={onLogRemoval} disabled={isReadOnly}>
                  Log removal
                </Button>
              )}
              {!hasShipped && (
                <Button
                  size="small"
                  startIcon={<SwapHoriz />}
                  onClick={onMove}
                  disabled={isReadOnly}
                >
                  Move container
                </Button>
              )}
              {/* The label screen is the print surface — format, size and a
                  full-size preview — so this hands off to it rather than
                  firing a print the reader hasn't seen. */}
              <Button size="small" startIcon={<Print />} onClick={onPrintLabel}>
                Print label
              </Button>
              <ContainerOverflowMenu
                container={container}
                hasShipped={hasShipped}
                isReadOnly={isReadOnly}
                onEdit={onEdit}
                onConsolidate={onConsolidate}
                onEmpty={onEmpty}
                onChangeStatus={onChangeStatus}
              />
            </Stack>
          )}
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label="Close drawer">
          <Close fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

/** The drawer's shape before its data arrives, so content resolves in place. */
function ContainerDrawerSkeleton() {
  const card = (height: number) => (
    <Box sx={{ backgroundColor: "grey.50", borderRadius: 1, px: 3, pt: 1, pb: 3 }}>
      <Skeleton variant="text" width={120} height={16} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={height} />
    </Box>
  );
  return (
    <Box>
      <Box sx={{ px: 3, py: 2 }}>
        <Skeleton variant="text" width={320} height={20} />
        <Skeleton variant="text" width={240} height={34} sx={{ mt: 2 }} />
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Skeleton variant="rounded" width={92} height={24} />
          <Skeleton variant="rounded" width={78} height={24} />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {[130, 104, 128, 96].map((w) => (
            <Skeleton key={w} variant="rounded" width={w} height={32} />
          ))}
        </Stack>
      </Box>
      <Divider />
      <Stack spacing={2} sx={{ px: 3, py: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>{card(148)}</Grid>
          <Grid size={{ xs: 12, md: 6 }}>{card(148)}</Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>{card(180)}</Grid>
          <Grid size={{ xs: 12, md: 6 }}>{card(180)}</Grid>
        </Grid>
        {card(80)}
      </Stack>
    </Box>
  );
}

/**
 * Everything about one container, in the specs' card grid.
 *
 * Split out from the drawer shell so the dialogs it owns mount alongside the
 * content that opens them, and so the shell can render a skeleton without this
 * component having to cope with an absent container.
 */
function ContainerDrawerContent({
  container,
  onClose,
  labelOpen: controlledLabelOpen,
  onLabelOpenChange,
  onOpenStream,
}: {
  container: WasteContainerDetailData;
  onClose: () => void;
  labelOpen?: boolean;
  onLabelOpenChange?: (open: boolean) => void;
  onOpenStream: () => void;
}) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();
  const showStreamLink = !!featureFlags[FeatureFlagKey.ManifestViewsV2];
  // Renamed to distinguish container-level archival from the facility-level
  // read-only state below (a facility can be archived while its containers are
  // not, and vice-versa in the historical data).
  const containerIsArchived = !!container.archivedAt;
  const { isReadOnly } = useFacilityReadOnly(container.facilityId);

  // Controlled by the containers page, which keeps the label dialog in the
  // URL so it can be linked to; uncontrolled anywhere else the drawer is
  // embedded (the accumulation-area page), where there is no route of its own
  // to put it in.
  const [uncontrolledLabelOpen, setUncontrolledLabelOpen] = useState(false);
  const labelOpen = controlledLabelOpen ?? uncontrolledLabelOpen;
  const setLabelOpen = onLabelOpenChange ?? setUncontrolledLabelOpen;

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [emptyDialogOpen, setEmptyDialogOpen] = useState(false);
  const [consolidateDialogOpen, setConsolidateDialogOpen] = useState(false);
  const [fillDialogDirection, setFillDialogDirection] = useState<"ADD" | "REMOVE" | null>(null);

  // The builder picks containers per facility, so it needs both: the facility
  // to scope its list, and the container to arrive already selected.
  const requestManifest = () => {
    if (!tenantId) return;
    const params = new URLSearchParams({
      facilityId: container.facilityId,
      containerIds: container.id,
    });
    navigate(`/o/${tenantId}/waste/shipments/new?${params.toString()}`);
  };

  return (
    <>
      <ContainerDrawerHeader
        container={container}
        isReadOnly={isReadOnly}
        onClose={onClose}
        onRequestManifest={requestManifest}
        onLogRemoval={() => setFillDialogDirection("REMOVE")}
        onMove={() => setMoveDialogOpen(true)}
        onPrintLabel={() => setLabelOpen(true)}
        onEdit={() => setEditDialogOpen(true)}
        onConsolidate={() => setConsolidateDialogOpen(true)}
        onEmpty={() => setEmptyDialogOpen(true)}
        onChangeStatus={() => setStatusDialogOpen(true)}
      />
      <Divider />
      <ContainerDrawerBody
        container={container}
        tenantId={tenantId}
        onOpenStream={showStreamLink ? onOpenStream : undefined}
        isArchived={containerIsArchived}
        onOpenLabel={() => setLabelOpen(true)}
      />
      <ContainerDrawerDialogs
        container={container}
        editOpen={editDialogOpen}
        moveOpen={moveDialogOpen}
        statusOpen={statusDialogOpen}
        emptyOpen={emptyDialogOpen}
        consolidateOpen={consolidateDialogOpen}
        fillDirection={fillDialogDirection}
        onCloseEdit={() => setEditDialogOpen(false)}
        onCloseMove={() => setMoveDialogOpen(false)}
        onCloseStatus={() => setStatusDialogOpen(false)}
        onCloseEmpty={() => setEmptyDialogOpen(false)}
        onCloseConsolidate={() => setConsolidateDialogOpen(false)}
        onCloseFill={() => setFillDialogDirection(null)}
        onOpenEdit={() => setEditDialogOpen(true)}
        onOpenAddFill={() => setFillDialogDirection("ADD")}
      />
      <ContainerLabelDialog
        containerId={container.id}
        open={labelOpen}
        onClose={() => setLabelOpen(false)}
      />
    </>
  );
}

/**
 * The drawer's scrolling card grid, in the order the specs lay it out: the two
 * gauges, then the two histories, then the records.
 */
function ContainerDrawerBody({
  container,
  tenantId,
  onOpenStream,
  isArchived,
  onOpenLabel,
}: {
  container: WasteContainerDetailData;
  tenantId: string | undefined;
  onOpenStream: (() => void) | undefined;
  isArchived: boolean;
  onOpenLabel: () => void;
}) {
  return (
    <Stack spacing={2} sx={{ px: 3, pt: 2, pb: 3, overflowY: "auto" }}>
      {isArchived && (
        <Alert severity="info" variant="outlined">
          This container was archived on {formatContainerDate(container.archivedAt)} and is
          read-only.
          {container.shippedOnShipment && (
            <>
              {" It left the site on "}
              <ShipmentLink shipment={container.shippedOnShipment} tenantId={tenantId} />.
            </>
          )}
        </Alert>
      )}

      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12, md: 6 }}>
          <AccumulationClockSection container={container} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ContainerFillCard container={container} />
        </Grid>
      </Grid>

      {/* Stretch, like the rows above and below: a container almost never has
          as many fill events as activity entries, and letting the shorter card
          size to its own content left a ragged white gap under it. */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12, md: 6 }}>
          <FillHistorySection container={container} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ContainerHistorySection container={container} />
        </Grid>
      </Grid>

      <ContainerWasteCard container={container} tenantId={tenantId} onOpenStream={onOpenStream} />

      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12, md: 6 }}>
          <ContainerLabelSection container={container} onOpenLabel={onOpenLabel} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ContainerShippingCard container={container} tenantId={tenantId} />
        </Grid>
      </Grid>

      <ContainerDetailsCard container={container} tenantId={tenantId} />
    </Stack>
  );
}

/**
 * Every dialog the drawer's actions can open.
 *
 * Gathered here so the content component stays a description of what the
 * drawer shows rather than a list of what it can launch. Each is mounted only
 * while open (except Move, which animates its own transition), so none of them
 * runs a query for a container the reader never asked about.
 */
function ContainerDrawerDialogs({
  container,
  editOpen,
  moveOpen,
  statusOpen,
  emptyOpen,
  consolidateOpen,
  fillDirection,
  onCloseEdit,
  onCloseMove,
  onCloseStatus,
  onCloseEmpty,
  onCloseConsolidate,
  onCloseFill,
  onOpenEdit,
  onOpenAddFill,
}: {
  container: WasteContainerDetailData;
  editOpen: boolean;
  moveOpen: boolean;
  statusOpen: boolean;
  emptyOpen: boolean;
  consolidateOpen: boolean;
  fillDirection: "ADD" | "REMOVE" | null;
  onCloseEdit: () => void;
  onCloseMove: () => void;
  onCloseStatus: () => void;
  onCloseEmpty: () => void;
  onCloseConsolidate: () => void;
  onCloseFill: () => void;
  /** Follow-ups the status dialog can hand back when reconciling an amount. */
  onOpenEdit: () => void;
  onOpenAddFill: () => void;
}) {
  return (
    <>
      {editOpen && <WasteContainerDialog container={container} onClose={onCloseEdit} />}
      <MoveContainerDialog container={container} open={moveOpen} onClose={onCloseMove} />
      {statusOpen && (
        <SetContainerStatusDialog
          container={container}
          onClose={onCloseStatus}
          onReconcileFollowUp={(choice) => {
            if (choice === ReconcileChoice.AddEvent) {
              onOpenAddFill();
            } else if (choice === ReconcileChoice.AdjustCapacity) {
              onOpenEdit();
            }
          }}
        />
      )}
      {emptyOpen && <EmptyContainerDialog container={container} onClose={onCloseEmpty} />}
      {consolidateOpen && (
        <ConsolidateContainersDialog container={container} onClose={onCloseConsolidate} />
      )}
      {fillDirection && (
        <ContainerFillEventDialog
          container={container}
          initialDirection={fillDirection}
          onClose={onCloseFill}
        />
      )}
    </>
  );
}

/** Sits outside the stream's scroller, so it stays in reach however far down the reader goes. */
const STREAM_TRAIL_SX = { px: 3, py: 1.5, borderBottom: 1, borderColor: "divider" };

/** The drawer's close, beside the trail; the stream's own header leaves its close out. */
function CloseDrawerButton({ onClose }: { onClose: () => void }) {
  return (
    <IconButton size="small" onClick={onClose} aria-label="Close drawer" sx={{ flexShrink: 0 }}>
      <Close fontSize="small" />
    </IconButton>
  );
}

/**
 * Where the reader is: the container, then its stream. The container is the
 * way back to it.
 */
function StreamBreadcrumbs({
  containerLabel,
  streamName,
  onBack,
  onClose,
}: {
  containerLabel: string;
  streamName: string;
  onBack: () => void;
  onClose: () => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={STREAM_TRAIL_SX}
    >
      <Breadcrumbs
        aria-label="breadcrumb"
        separator="/"
        // One line: a long stream name gives way, not the trail.
        sx={{
          minWidth: 0,
          "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
          "& .MuiBreadcrumbs-li:last-of-type": { minWidth: 0 },
        }}
      >
        <Link component="button" variant="body2" underline="hover" onClick={onBack}>
          {containerLabel}
        </Link>
        <Typography variant="body2" color="text.primary" noWrap>
          {streamName}
        </Typography>
      </Breadcrumbs>
      <CloseDrawerButton onClose={onClose} />
    </Stack>
  );
}

/** The stream view before the container that names its stream has loaded. */
function ContainerStreamViewSkeleton({ onClose }: { onClose: () => void }) {
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={STREAM_TRAIL_SX}
      >
        <Skeleton variant="text" width={240} sx={{ typography: "body2" }} />
        <CloseDrawerButton onClose={onClose} />
      </Stack>
      <WasteStreamDetailsModalSkeleton showCloseButton={false} />
    </>
  );
}

/** The container's stream in place of the container. */
function ContainerStreamView({
  containerLabel,
  tenantId,
  wasteStreamId,
  streamName,
  onBack,
  onClose,
}: {
  containerLabel: string;
  tenantId: string;
  wasteStreamId: string;
  streamName: string;
  onBack: () => void;
  onClose: () => void;
}) {
  // The swap's first render is only the trail and a skeleton, so the panel
  // starts widening on the click; the details render behind it without
  // holding that frame up, even when they're already cached.
  const shownStreamId = useDeferredValue<string | null>(wasteStreamId, null);
  return (
    <>
      <StreamBreadcrumbs
        containerLabel={containerLabel}
        streamName={streamName}
        onBack={onBack}
        onClose={onClose}
      />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {shownStreamId ? (
          <WasteStreamPanel
            tenantId={tenantId}
            wasteStreamId={shownStreamId}
            onClose={onClose}
            showCloseButton={false}
          />
        ) : (
          <WasteStreamDetailsModalSkeleton showCloseButton={false} />
        )}
      </Box>
    </>
  );
}

/**
 * Whether the drawer shows the container's stream in its place. From the URL
 * when the page puts it there; otherwise remembered here, keyed by container
 * so moving to another shows that container, and forgotten once the drawer
 * finishes closing so it reopens on the container.
 */
function useStreamView(
  containerId: string | undefined,
  controlledOpen: boolean | undefined,
  onOpenChange: ((open: boolean) => void) | undefined
) {
  const [streamOf, setStreamOf] = useState<string | null>(null);
  return {
    streamOpen: controlledOpen ?? (!!containerId && streamOf === containerId),
    setStreamOpen:
      onOpenChange ?? ((next: boolean) => setStreamOf(next ? (containerId ?? null) : null)),
    forget: () => setStreamOf(null),
  };
}

/**
 * Re-reads the container once the drawer leaves its stream: edits there change
 * what the container derives from the stream, such as its label's DOT name,
 * and the container's query stays mounted under the stream view.
 */
function useRefetchAfterStream(
  streamOpen: boolean,
  containerId: string | undefined,
  refetch: () => Promise<unknown>
) {
  const wasStreamOpen = useRef(streamOpen);
  useEffect(() => {
    if (wasStreamOpen.current && !streamOpen && containerId) {
      // A failed refetch surfaces through the query's own error.
      void refetch().catch(() => undefined);
    }
    wasStreamOpen.current = streamOpen;
  }, [streamOpen, containerId, refetch]);
}

/**
 * One container's detail, as a panel over whatever opened it.
 *
 * A drawer rather than a page so the container can be inspected from the list,
 * from an accumulation area, or from anywhere else a container is named,
 * without the reader losing the context they were working in. The list page
 * keeps the container's id in the URL while it's open, so a QR code or a deep
 * link still lands here.
 */
export function WasteContainerDrawer({
  containerId,
  open,
  onClose,
  labelOpen,
  onLabelOpenChange,
  streamOpen: controlledStreamOpen,
  onStreamOpenChange,
}: {
  containerId: string | undefined;
  open: boolean;
  onClose: () => void;
  /** Drives the label dialog from the URL. Omit to let the drawer own it. */
  labelOpen?: boolean;
  onLabelOpenChange?: (open: boolean) => void;
  /** Drives the stream view from the URL. Omit to let the drawer own it. */
  streamOpen?: boolean;
  onStreamOpenChange?: (open: boolean) => void;
}) {
  const { tenantId } = useTenant();
  // Network on every open: mutations made while the drawer is closed (attach
  // and detach on the episodic events page, edits from the list) can't refetch
  // an unmounted query, so cache-first would reopen on the pre-change container.
  const { data, loading, error, refetch } = useQuery(GET_WASTE_CONTAINER_QUERY, {
    variables: { id: containerId ?? "" },
    skip: !containerId,
    fetchPolicy: "cache-and-network",
  });
  const container = data?.wasteContainer;
  const { streamOpen, setStreamOpen, forget } = useStreamView(
    containerId,
    controlledStreamOpen,
    onStreamOpenChange
  );
  useRefetchAfterStream(streamOpen, containerId, refetch);
  const streamId = streamOpen ? container?.wasteStream?.id : undefined;
  // A link straight to the stream opens wide, before the container naming
  // the stream has loaded, rather than widening once it has.
  const awaitingStream = streamOpen && loading && !container;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Suppresses MUI's body scroll lock, which otherwise jitters the list
      // behind by the scrollbar's width every time the drawer opens.
      disableScrollLock
      // Sized by the frame inside it: the slide-in owns the paper's inline
      // `transition`, so the width can't animate on the paper itself.
      sx={{ "& .MuiDrawer-paper": { width: { xs: "100%", sm: "auto" }, boxSizing: "border-box" } }}
      slotProps={{ transition: { onExited: forget } }}
    >
      <Box
        sx={{
          width: streamId || awaitingStream ? STREAM_VIEW_WIDTH : DRAWER_WIDTH,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          overflowY: "auto",
          transition: (theme) => theme.transitions.create("width"),
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        {awaitingStream && <ContainerStreamViewSkeleton onClose={onClose} />}
        {loading && !container && !awaitingStream && <ContainerDrawerSkeleton />}
        {/* Deliberately keyed on the absence of a container rather than on
          `error`: a single nested field that fails to resolve comes back as a
          GraphQL error alongside a perfectly good container, and blanking the
          whole drawer over one missing value tells the reader their container
          doesn't exist. If the container itself is here, show it.

          Also keyed on `containerId`: every caller clears the id and closes in
          the same render, and MUI keeps the children mounted through the exit
          transition — so without it the panel flashes "not found" on its way
          out. There is no missing container until someone has asked for one. */}
        {!loading && !container && containerId && (
          <Stack spacing={1} sx={{ px: 3, py: 4 }} alignItems="center">
            <Typography variant="h6">Container not found</Typography>
            <Typography variant="body2" color="text.secondary">
              {error
                ? "Something went wrong loading this container."
                : "The container may have been archived, or the link may be incorrect."}
            </Typography>
          </Stack>
        )}
        {container &&
          (streamId && tenantId ? (
            <ContainerStreamView
              containerLabel={container.containerLabel}
              tenantId={tenantId}
              wasteStreamId={streamId}
              streamName={container.wasteStream?.name ?? ""}
              onBack={() => setStreamOpen(false)}
              onClose={onClose}
            />
          ) : (
            <ContainerDrawerContent
              container={container}
              onClose={onClose}
              labelOpen={labelOpen}
              onLabelOpenChange={onLabelOpenChange}
              onOpenStream={() => setStreamOpen(true)}
            />
          ))}
      </Box>
    </Drawer>
  );
}
